import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  computeRecurrence,
  DEFAULT_USER_SETTINGS,
  localCalendarDate,
  orderDashboardTypes,
  type ActivityLog,
  type ActivityType,
  type Household,
  type Membership,
  type RecurrenceStats,
  type UserProfile,
  type UserSettings,
} from "@lifelog/shared";
import { EllipsisVertical, MessageCircle, Pin, StickyNote } from "lucide-react";
import { api, ApiError, isOfflineError } from "../lib/api.js";
import { hashForView } from "../lib/hashRoute.js";
import { PageSkeleton } from "./Skeleton.js";
import { ModalShell } from "./ModalShell.js";
import { ActivityButtonGrid } from "./ActivityButtonGrid.js";
import { QuickLogModal } from "./QuickLogModal.js";
import { QuickNoteLog } from "./QuickNoteLog.js";
import { BackfillModal } from "./BackfillModal.js";
import { CustomizePanel } from "./CustomizePanel.js";

const LOG_LOOKBACK_MS = 3 * 365 * 24 * 60 * 60 * 1000;
const TOAST_MS = 8000;
const IDEMPOTENCY_WINDOW_MS = 2000;

function hasRequiredFields(type: ActivityType): boolean {
  return type.fields.some((f) => f.required);
}

type Toast = { log: ActivityLog; label: string };

export function DashboardPane({ household }: { household: Household }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [types, setTypes] = useState<ActivityType[]>([]);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_USER_SETTINGS);
  const [me, setMe] = useState<UserProfile | null>(null);
  const [members, setMembers] = useState<Membership[]>([]);
  const [toast, setToast] = useState<Toast | null>(null);
  const [quickLogType, setQuickLogType] = useState<ActivityType | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingTypeId, setPendingTypeId] = useState<string | null>(null);
  const [pinBusyId, setPinBusyId] = useState<string | null>(null);

  const toastTimer = useRef<number | null>(null);
  const idempotencyKeys = useRef(new Map<string, { key: string; expires: number }>());

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const from = new Date(Date.now() - LOG_LOOKBACK_MS).toISOString();
        const to = new Date().toISOString();
        const [typesRes, logsRes, settingsRes, meRes, membersRes] = await Promise.all([
          api<{ types: ActivityType[] }>("/activity-types"),
          api<{ logs: ActivityLog[] }>(
            `/logs?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=500`,
          ),
          api<UserSettings>("/settings"),
          api<{ profile: UserProfile }>("/me"),
          api<{ members: Membership[] }>("/household/members"),
        ]);
        setTypes(typesRes.types);
        setLogs(logsRes.logs);
        setSettings(settingsRes);
        setMe(meRes.profile);
        setMembers(membersRes.members);
      } catch (err) {
        if (isOfflineError(err)) {
          setError("You're offline. Try again when you're back online.");
        } else if (err instanceof ApiError && err.status === 401) {
          return;
        } else {
          setError(err instanceof Error ? err.message : "Couldn't load the dashboard.");
        }
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load();
  }, [load, household.householdId]);

  const viewer = useMemo(
    () => ({
      userId: me?.userId ?? "",
      isHouseholdOwner: me?.userId === household.ownerUserId,
    }),
    [me?.userId, household.ownerUserId],
  );

  const statsByTypeId = useMemo(() => {
    const byType = new Map<string, string[]>();
    for (const log of logs) {
      const arr = byType.get(log.activityTypeId) ?? [];
      arr.push(log.occurredAt);
      byType.set(log.activityTypeId, arr);
    }
    const nowIso = new Date().toISOString();
    const out: Record<string, RecurrenceStats> = {};
    for (const t of types) {
      out[t.activityTypeId] = computeRecurrence(
        byType.get(t.activityTypeId) ?? [],
        household.timezone,
        nowIso,
      );
    }
    return out;
  }, [logs, types, household.timezone]);

  const ordered = useMemo(
    () => orderDashboardTypes(types, statsByTypeId, viewer).filter((t) => !t.builtIn),
    [types, statsByTypeId, viewer],
  );

  const loggedToday = useMemo(() => {
    const today = localCalendarDate(new Date().toISOString(), household.timezone);
    const ids = new Set<string>();
    for (const log of logs) {
      if (localCalendarDate(log.occurredAt, household.timezone) === today) {
        ids.add(log.activityTypeId);
      }
    }
    return ids;
  }, [logs, household.timezone]);

  function showToast(log: ActivityLog, label: string) {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast({ log, label });
    toastTimer.current = window.setTimeout(() => setToast(null), TOAST_MS);
  }

  function idemKey(typeId: string): string {
    const now = Date.now();
    const existing = idempotencyKeys.current.get(typeId);
    if (existing && existing.expires > now) return existing.key;
    const key = crypto.randomUUID();
    idempotencyKeys.current.set(typeId, { key, expires: now + IDEMPOTENCY_WINDOW_MS });
    return key;
  }

  async function oneTap(type: ActivityType) {
    setPendingTypeId(type.activityTypeId);
    try {
      const log = await api<ActivityLog>("/logs", {
        method: "POST",
        headers: { "Idempotency-Key": idemKey(type.activityTypeId) },
        json: {
          activityTypeId: type.activityTypeId,
          values: {},
          source: "quick_button",
          performedByUserId: settings.defaultPerformedBySelf ? me?.userId : undefined,
        },
      });
      showToast(log, type.shortName);
      await load(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't log that.");
    } finally {
      setPendingTypeId(null);
    }
  }

  function onTap(type: ActivityType) {
    if (settings.oneTapOpensModal || hasRequiredFields(type)) setQuickLogType(type);
    else void oneTap(type);
  }

  async function undoToast() {
    if (!toast) return;
    const { log } = toast;
    setToast(null);
    try {
      await api(
        `/logs/${encodeURIComponent(log.occurredAt)}/${encodeURIComponent(log.logId)}`,
        { method: "DELETE" },
      );
      await load(true);
    } catch {
      /* undo is best-effort */
    }
  }

  async function pin(typeId: string, pinned: boolean) {
    setPinBusyId(typeId);
    try {
      await api(`/activity-types/${encodeURIComponent(typeId)}/pin`, {
        method: pinned ? "POST" : "DELETE",
      });
      await load(true);
    } finally {
      setPinBusyId(null);
    }
  }

  async function movePin(typeId: string, direction: "up" | "down") {
    setPinBusyId(typeId);
    try {
      await api(`/activity-types/${encodeURIComponent(typeId)}/pin`, {
        method: "POST",
        json: { move: direction },
      });
      await load(true);
    } finally {
      setPinBusyId(null);
    }
  }

  // Hooks above this return (H-1). The lint rule from Task 3.1 enforces it.
  if (loading) return <PageSkeleton />;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 p-4">
      <div className="flex items-center gap-2">
        <h1 className="flex-1 text-lg font-semibold text-gray-900 dark:text-gray-100">Dashboard</h1>
        <button
          type="button"
          onClick={() => setNoteOpen(true)}
          className="flex h-11 items-center gap-1 rounded-lg border border-gray-200 px-3 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-300"
        >
          <StickyNote size={16} />
          Note
        </button>
        <div className="relative">
          <button
            type="button"
            aria-label="More actions"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300"
          >
            <EllipsisVertical size={18} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 z-20 mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900">
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                onClick={() => {
                  setMenuOpen(false);
                  setBackfillOpen(true);
                }}
              >
                Backfill
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                onClick={() => {
                  setMenuOpen(false);
                  setCustomizeOpen(true);
                }}
              >
                <Pin size={14} />
                Pin order
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </p>
      )}

      <ActivityButtonGrid
        types={ordered}
        statsByTypeId={statsByTypeId}
        loggedToday={loggedToday}
        pendingTypeId={pendingTypeId}
        onTap={onTap}
      />

      <a
        href={hashForView("chat")}
        className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-(--accent)/50 bg-(--accent)/5 px-4 py-3 text-sm font-medium text-(--accent)"
      >
        <MessageCircle size={16} />
        Log something else →
      </a>

      {toast && (
        <div className="fixed inset-x-0 bottom-20 z-40 mx-auto flex max-w-sm items-center gap-3 rounded-xl bg-gray-900 px-4 py-3 text-sm text-white shadow-lg dark:bg-gray-100 dark:text-gray-900 sm:bottom-4">
          <span className="flex-1">Logged {toast.label}</span>
          <button type="button" onClick={() => void undoToast()} className="font-medium underline">
            Undo
          </button>
        </div>
      )}

      {quickLogType && me && (
        <QuickLogModal
          type={quickLogType}
          members={members}
          currentUserId={me.userId}
          defaultPerformedBySelf={settings.defaultPerformedBySelf}
          onClose={() => setQuickLogType(null)}
          onSaved={(log) => {
            showToast(log, quickLogType.shortName);
            setQuickLogType(null);
            void load(true);
          }}
        />
      )}

      {noteOpen && (
        <QuickNoteLog
          onClose={() => setNoteOpen(false)}
          onSaved={(log) => {
            showToast(log, "Note");
            setNoteOpen(false);
            void load(true);
          }}
        />
      )}

      {backfillOpen && me && (
        <BackfillModal
          types={types}
          members={members}
          currentUserId={me.userId}
          defaultPerformedBySelf={settings.defaultPerformedBySelf}
          onClose={() => setBackfillOpen(false)}
          onSaved={(saved) => {
            if (saved[0]) showToast(saved[0], `${saved.length} logged`);
            setBackfillOpen(false);
            void load(true);
          }}
        />
      )}

      {customizeOpen && (
        <ModalShell title="Pin order" size="lg" onClose={() => setCustomizeOpen(false)}>
          <CustomizePanel
            types={ordered}
            onPin={(id) => void pin(id, true)}
            onUnpin={(id) => void pin(id, false)}
            onMove={(id, dir) => void movePin(id, dir)}
            busyId={pinBusyId}
          />
        </ModalShell>
      )}
    </div>
  );
}
